/**
 * Reminders and chasers: one email per item at each step, to the person it
 * waits on. Each kind has its steps in Admin: when it lands on someone, days
 * before its date, on the day, and every so many days while late (or while
 * waiting), up to a limit. On any day only the latest step due is sent, so a
 * pause (a weekend, the server down) never sends a backlog, and nothing from
 * before reminders were switched on is ever sent.
 */
import { addDays, daysBetween, dayLabel, londonToday, type ISODate } from "./dates";
import { money } from "./money";
import { actionRef, dueOf } from "./actions";
import { decisionRef, waitingOn } from "./decisions";
import { obligationNow, obligationRef } from "./obligations";
import { RELEASE_OPEN, releaseRef, releaseTiming } from "./releases";
import { bandOf, nextReview, riskRef, score } from "./risks";
import { isLive, monthOf, nextMonth } from "./measures";
import { PACK_TITLE, canApproveSection, entryFor, isCompiled, packCovers, packPeriod } from "./packs";
import type { RegistersModel } from "./registers";
import { defaultChair, minuted, minutesDue } from "./minutes";

export type ReminderKind =
  | "pack_section"
  | "pack_returned"
  | "pack_approval"
  | "figure"
  | "action"
  | "action_check"
  | "decision"
  | "consent"
  | "decision_review"
  | "obligation"
  | "release"
  | "risk_review"
  | "risk_escalation"
  | "minutes"
  | "minutes_sign"
  | "minutes_circulate";

/** Dated: around a due date. Waiting: from when it landed. Event: when it lands, once. */
export type ReminderShape = "dated" | "waiting" | "event";

export const REMINDER_KINDS: Record<ReminderKind, { label: string; shape: ReminderShape; lands: string | null }> = {
  pack_section: { label: "Pack sections and papers", shape: "dated", lands: "Opened" },
  pack_returned: { label: "Sections sent back", shape: "event", lands: "Sent back" },
  pack_approval: { label: "Sections to approve", shape: "waiting", lands: "To approve" },
  figure: { label: "Figures to enter or confirm", shape: "waiting", lands: "To enter" },
  action: { label: "Actions", shape: "dated", lands: "New action" },
  action_check: { label: "Actions to confirm", shape: "waiting", lands: "To confirm" },
  decision: { label: "Decisions waiting", shape: "waiting", lands: "Waiting on you" },
  consent: { label: "Consents to record", shape: "waiting", lands: "Consent to record" },
  decision_review: { label: "Decision reviews", shape: "dated", lands: null },
  obligation: { label: "Obligations", shape: "dated", lands: null },
  release: { label: "Statutory changes", shape: "dated", lands: null },
  risk_review: { label: "Risk reviews", shape: "dated", lands: null },
  risk_escalation: { label: "Risks rated in the top bands", shape: "event", lands: "Rated" },
  minutes: { label: "Minutes to write", shape: "dated", lands: "Meeting held" },
  minutes_sign: { label: "Minutes to sign off", shape: "waiting", lands: "To sign off" },
  minutes_circulate: { label: "Minutes to circulate", shape: "waiting", lands: "Signed off" },
};
export const REMINDER_KIND_KEYS = Object.keys(REMINDER_KINDS) as ReminderKind[];

export interface ReminderRule {
  kind: ReminderKind;
  on: boolean;
  /** When it lands on someone: opened, sent back, waiting on them. */
  lands: boolean;
  /** Days before its date; "lead" is an obligation's own lead time. */
  before: number | "lead" | null;
  onDay: boolean;
  /** Days between chasers while late (dated) or while waiting. */
  every: number | null;
  /** How many chasers at most; null for no limit. */
  upTo: number | null;
}

export interface ReminderSettings {
  on: boolean;
  /** When reminders were last switched on: nothing earlier is sent. */
  since: string | null;
  /** London times, working days only. Steps go from `sendFrom`; anything that lands outside the hours waits for them. */
  sendFrom: string;
  sendUntil: string;
  rules: ReminderRule[];
}

const rule = (kind: ReminderKind, r: Partial<Omit<ReminderRule, "kind">>): ReminderRule => ({ kind, on: true, lands: false, before: null, onDay: false, every: null, upTo: null, ...r });
export const DEFAULT_REMINDER_RULES: ReminderRule[] = [
  rule("pack_section", { lands: true, before: 3, onDay: true, every: 2, upTo: 3 }),
  rule("pack_returned", { lands: true }),
  rule("pack_approval", { lands: true, every: 2, upTo: 3 }),
  rule("figure", { lands: true, every: 3, upTo: 3 }),
  rule("action", { lands: true, before: 2, onDay: true, every: 3, upTo: 5 }),
  rule("action_check", { lands: true, every: 5, upTo: 2 }),
  rule("decision", { lands: true, every: 2, upTo: 5 }),
  rule("consent", { lands: true, every: 3, upTo: 3 }),
  rule("decision_review", { before: 7, onDay: true, every: 7, upTo: 3 }),
  rule("obligation", { before: "lead", onDay: true, every: 7, upTo: 4 }),
  rule("release", { before: 14, onDay: true, every: 7, upTo: 4 }),
  rule("risk_review", { before: 7, onDay: true, every: 7, upTo: 3 }),
  rule("risk_escalation", { lands: true }),
  rule("minutes", { lands: true, onDay: true, every: 2, upTo: 3 }),
  rule("minutes_sign", { lands: true, every: 2, upTo: 3 }),
  rule("minutes_circulate", { lands: true, every: 2, upTo: 2 }),
];
export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = { on: false, since: null, sendFrom: "09:00", sendUntil: "18:00", rules: DEFAULT_REMINDER_RULES };

/** The rules in the catalogue's order, any missing from the saved ones as they start. */
export const rulesOf = (s: Pick<ReminderSettings, "rules">): ReminderRule[] => REMINDER_KIND_KEYS.map((k) => s.rules.find((r) => r.kind === k) ?? DEFAULT_REMINDER_RULES.find((r) => r.kind === k)!);

/* ------------------------------------------------------------------ */
/* What's open, for whom                                               */
/* ------------------------------------------------------------------ */

export interface ReminderItem {
  kind: ReminderKind;
  /** Stable across days: the thing itself (and the month, for a figure). */
  id: string;
  /** Who it waits on: user ids. */
  to: string[];
  ref: string | null;
  title: string;
  /** In the app, from its root. */
  href: string;
  /** The date it turns on (dated kinds). */
  date: ISODate | null;
  /** When it landed: a timestamp, or a date. */
  lands: string | null;
  /** Who made it land: they aren't told about their own doing. */
  landsBy: string | null;
  /** An obligation's lead time. */
  leadDays?: number;
  /** The pill when it lands, if not the kind's. */
  landsLabel?: string;
  /** Label, value. */
  facts: [string, string][];
  /** The button. */
  open: string;
}

const short = (s: string, n = 60) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const dayOfStamp = (s: string) => (s.length === 10 ? s : londonToday(new Date(s)));

/** Everything open that waits on someone, with its date and who it waits on. */
export function reminderItems(reg: RegistersModel, today: ISODate): ReminderItem[] {
  const out: ReminderItem[] = [];
  const person = (id: string | null) => (id ? reg.people.find((p) => p.id === id) : undefined);
  const name = (id: string | null) => person(id)?.name ?? "—";
  const directors = reg.people.filter((p) => p.active && p.role === "director");

  // packs: sections to write, sent back, waiting on a director
  for (const p of reg.packs.filter((x) => x.status === "open")) {
    const pack = `${packPeriod(p)} ${PACK_TITLE[p.kind].toLowerCase()}`;
    for (const sec of reg.packSections.filter((x) => x.active && x.kind === p.kind && !isCompiled(x))) {
      const e = entryFor(p, sec.id);
      const href = `/${p.kind === "board" ? "board" : "pack"}?month=${p.month}&section=${sec.id}`;
      const base = { ref: null, title: sec.title, href, open: "Open the section" };
      if (e.state === "todo" || e.state === "draft") {
        const owners = sec.ownerIds.filter((id) => person(id)?.active);
        out.push({ ...base, kind: "pack_section", id: `${p.id}:${sec.id}`, to: owners, date: p.dueOn, lands: p.createdAt, landsBy: null, facts: [["Pack", pack], ["Due", dayLabel(p.dueOn)], ["Meeting", dayLabel(p.meetingOn)]] });
        if (e.returnedAt && e.returnedNote)
          out.push({ ...base, kind: "pack_returned", id: `${p.id}:${sec.id}`, to: owners, date: null, lands: e.returnedAt, landsBy: e.returnedBy, facts: [["Pack", pack], ["Due", dayLabel(p.dueOn)], ["Sent back by", name(e.returnedBy)], ["Note", e.returnedNote]] });
      }
      if (e.state === "submitted" && e.submittedAt) {
        const approvers = directors.filter((d) => canApproveSection(d, sec, reg.packSettings)).map((d) => d.id);
        out.push({ ...base, kind: "pack_approval", id: `${p.id}:${sec.id}`, to: approvers, date: null, lands: e.submittedAt, landsBy: e.submittedBy, facts: [["Pack", pack], ["Submitted by", name(e.submittedBy)], ["Meeting", dayLabel(p.meetingOn)]] });
      }
    }
  }

  // last month's figures to type in, and any not yet confirmed
  const lastMonth = monthOf(addDays(`${monthOf(today)}-01`, -1));
  for (const d of reg.measures.filter((x) => x.active && x.ownerId)) {
    const values = reg.measureValues.filter((v) => v.measureId === d.id);
    const base = { kind: "figure" as const, to: [d.ownerId!], ref: null, title: d.name, href: `/measures?open=${d.id}`, date: null, landsBy: null, open: "Open the measure" };
    if (d.source === "entered" && isLive(d, lastMonth) && !values.some((v) => v.month === lastMonth))
      out.push({ ...base, id: `${d.id}:${lastMonth}:enter`, lands: `${monthOf(today)}-01`, facts: [["Measure", d.name], ["Month", packCovers(lastMonth)]] });
    else {
      const month = values.filter((v) => !v.confirmedAt).map((v) => v.month).sort().at(-1);
      if (month) out.push({ ...base, id: `${d.id}:${month}:confirm`, lands: `${nextMonth(month)}-01`, landsLabel: "To confirm", facts: [["Measure", d.name], ["Month", packCovers(month)]] });
    }
  }

  // actions: due, and done ones for their raiser to confirm
  for (const a of reg.actions) {
    const ref = actionRef(a.ref);
    const href = `/decisions/actions?open=${a.id}`;
    if (a.status === "open" && a.ownerId) {
      const due = dueOf(a, reg.milestones);
      out.push({ kind: "action", id: a.id, to: [a.ownerId], ref, title: a.title, href, date: due, lands: a.createdAt, landsBy: a.createdBy, facts: [["Action", ref], ["Due", due ? dayLabel(due) : "—"], ["Raised by", name(a.createdBy)]], open: `Open ${ref}` });
    }
    if (a.status === "done" && !a.checkedAt && a.createdBy && a.createdBy !== a.doneBy && a.doneAt)
      out.push({ kind: "action_check", id: a.id, to: [a.createdBy], ref, title: a.title, href, date: null, lands: a.doneAt, landsBy: a.doneBy, facts: [["Action", ref], ["Done by", name(a.doneBy)], ...(a.doneNote ? [["Note", a.doneNote] as [string, string]] : [])], open: `Open ${ref}` });
  }

  // decisions: waiting on an approver, consents to record, reviews
  for (const d of reg.decisions) {
    const ref = decisionRef(d.ref);
    const href = d.leverId ? `/finance/levers?lever=${d.leverId}` : `/decisions?open=${d.id}`;
    const recused = d.approval?.recusedIds ?? [];
    const facts: [string, string][] = [["Decision", ref], ...(d.amount !== null ? [["Amount", money(d.amount)] as [string, string]] : [])];
    const waiting = waitingOn(d);
    if (waiting.length && d.proposedAt)
      out.push({ kind: "decision", id: d.id, to: waiting, ref, title: d.title, href, date: null, lands: d.proposedAt, landsBy: d.proposedBy, facts: [...facts, ["Proposed by", name(d.proposedBy)]], open: `Open ${ref}` });
    const notRecused = directors.filter((p) => !recused.includes(p.id)).map((p) => p.id);
    if (d.status === "awaiting_consent") {
      const since = [d.proposedAt, ...d.approvals.map((a) => a.at)].filter((x): x is string => !!x).sort().at(-1) ?? d.updatedAt;
      out.push({ kind: "consent", id: d.id, to: notRecused, ref, title: d.title, href, date: null, lands: since, landsBy: null, facts, open: `Open ${ref}` });
    }
    if (d.status === "taken" && d.reviewBy) out.push({ kind: "decision_review", id: d.id, to: notRecused, ref, title: d.title, href, date: d.reviewBy, lands: null, landsBy: null, facts: [...facts, ["Review by", dayLabel(d.reviewBy)]], open: `Open ${ref}` });
  }

  // obligations, statutory changes, risk reviews and escalations
  for (const o of reg.obligations.filter((x) => x.active && x.ownerId)) {
    const due = obligationNow(o, { company: reg.company, yearStartMonth: reg.yearStartMonth }, today).due;
    if (!due) continue;
    const ref = obligationRef(o.ref);
    out.push({ kind: "obligation", id: `${o.id}:${due}`, to: [o.ownerId!], ref, title: o.title, href: `/obligations?open=${o.id}`, date: due, lands: null, landsBy: null, leadDays: o.leadDays, facts: [["Obligation", ref], ["Due", dayLabel(due)]], open: `Open ${ref}` });
  }
  for (const r of reg.releases.filter((x) => x.ownerId && RELEASE_OPEN.includes(x.status))) {
    const t = releaseTiming(r, reg.releaseFrameworks.find((f) => f.id === r.frameworkId), today);
    if (!t.deployBy) continue;
    const ref = releaseRef(r.ref);
    out.push({ kind: "release", id: r.id, to: [r.ownerId!], ref, title: r.what, href: `/releases?open=${r.id}`, date: t.deployBy, lands: null, landsBy: null, facts: [["Change", ref], ["Live by", dayLabel(t.deployBy)]], open: `Open ${ref}` });
  }
  const top = reg.riskScale.bands.length - 2;
  for (const r of reg.risks.filter((x) => x.status === "open")) {
    const ref = riskRef(r, reg.riskDomains.find((d) => d.id === r.domainId));
    const band = bandOf(score(r.residual), reg.riskScale);
    const href = `/risks?open=${r.id}`;
    if (r.ownerId) {
      const next = nextReview(r, reg.riskTiers);
      out.push({ kind: "risk_review", id: `${r.id}:${next}`, to: [r.ownerId], ref, title: r.statement, href, date: next, lands: null, landsBy: null, facts: [["Risk", ref], ["Review due", dayLabel(next)], ["Rated", band.band.name]], open: `Open ${ref}` });
    }
    if (r.escalatedAt && band.index >= top)
      out.push({ kind: "risk_escalation", id: r.id, to: directors.map((p) => p.id).filter((id) => id !== r.escalatedBy), ref, title: r.statement, href, date: null, lands: r.escalatedAt, landsBy: r.escalatedBy, landsLabel: `Rated ${band.band.name}`, facts: [["Risk", ref], ["Rated", band.band.name], ["Owner", name(r.ownerId)], ["Rated by", name(r.escalatedBy)]], open: `Open ${ref}` });
  }
  // minutes: to write, to sign off (never the chair), to circulate
  const s = reg.packSettings;
  const holidays = new Set(reg.holidays.map((h) => h.date));
  const drafters = s.minutesDrafterId ? [s.minutesDrafterId] : directors.map((p) => p.id);
  for (const mt of reg.meetings.filter((x) => minuted(x) && x.date <= today)) {
    const row = reg.minutes.find((x) => x.meetingId === mt.id);
    const state = row?.state ?? "awaiting";
    const due = minutesDue(mt.date, s.minutesDays, holidays);
    const chair = row ? row.chairId : defaultChair(mt, s);
    const base = { id: mt.id, ref: null, title: `Minutes: ${mt.title}`, href: `/records/minutes?meeting=${mt.id}`, facts: [["Meeting", dayLabel(mt.date)], ["Circulate by", dayLabel(due)]] as [string, string][], open: "Open the minutes" };
    if (state === "awaiting" || state === "draft") out.push({ ...base, kind: "minutes", to: drafters, date: due, lands: mt.date, landsBy: null });
    if (state === "with_signer" && row?.sentAt) out.push({ ...base, kind: "minutes_sign", to: row.signerId ? [row.signerId] : directors.map((p) => p.id).filter((id) => id !== chair), date: null, lands: row.sentAt, landsBy: null });
    if (state === "signed" && row?.signedAt) out.push({ ...base, kind: "minutes_circulate", to: drafters, date: null, lands: row.signedAt, landsBy: row.signedBy });
  }
  return out.filter((i) => i.to.length > 0);
}

/* ------------------------------------------------------------------ */
/* Steps                                                               */
/* ------------------------------------------------------------------ */

export type StepKind = "lands" | "before" | "day" | "late" | "wait";
export interface ReminderStep {
  /** Unique for the item and recipient: a moved date makes new steps. */
  key: string;
  on: ISODate;
  step: StepKind;
}
const ORDER: Record<StepKind, number> = { lands: 0, wait: 1, before: 2, day: 3, late: 4 };

/** Every step of an item under its rule, up to `horizon` when there's no limit. */
export function stepsFor(item: Pick<ReminderItem, "kind" | "date" | "lands" | "leadDays">, r: ReminderRule, horizon: ISODate): ReminderStep[] {
  if (!r.on) return [];
  const shape = REMINDER_KINDS[item.kind].shape;
  const out: ReminderStep[] = [];
  const landsOn = item.lands ? dayOfStamp(item.lands) : null;
  if (r.lands && item.lands && landsOn && REMINDER_KINDS[item.kind].lands) out.push({ key: `lands:${item.lands}`, on: landsOn, step: "lands" });
  const repeat = (from: ISODate, key: string, step: StepKind) => {
    if (!r.every || r.every < 1) return;
    for (let n = 1; r.upTo === null ? addDays(from, n * r.every) <= horizon : n <= r.upTo; n++) out.push({ key: `${key}:${n}`, on: addDays(from, n * r.every), step });
  };
  if (shape === "dated" && item.date) {
    const before = r.before === "lead" ? (item.leadDays ?? null) : r.before;
    if (before && before > 0) out.push({ key: `before:${item.date}:${before}`, on: addDays(item.date, -before), step: "before" });
    if (r.onDay) out.push({ key: `day:${item.date}`, on: item.date, step: "day" });
    repeat(item.date, `late:${item.date}`, "late");
  }
  if (shape === "waiting" && item.lands && landsOn) repeat(landsOn, `wait:${item.lands}`, "wait");
  return out.sort((a, b) => a.on.localeCompare(b.on) || ORDER[a.step] - ORDER[b.step]);
}

/**
 * The one step to send today, if any: the latest one due, never one dated
 * before reminders were switched on, and never news of someone's own doing.
 */
export function stepDue(item: ReminderItem, r: ReminderRule, today: ISODate, since: string, recipient: string): ReminderStep | null {
  const sinceDay = dayOfStamp(since);
  const due = stepsFor(item, r, today).filter((s) => s.on <= today && s.on >= sinceDay && !(s.step === "lands" && item.lands! < since));
  const last = due.at(-1);
  if (!last || (last.step === "lands" && item.landsBy === recipient)) return null;
  return last;
}

/* ------------------------------------------------------------------ */
/* What the email says                                                 */
/* ------------------------------------------------------------------ */

export type ReminderTone = "new" | "due" | "late";
export interface ReminderContent {
  subject: string;
  pill: string;
  tone: ReminderTone;
  title: string;
  facts: [string, string][];
  open: string;
  href: string;
}

const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;

/** The pill, worked out on the day it's sent. */
export function pillFor(item: Pick<ReminderItem, "kind" | "date" | "lands" | "landsLabel">, step: StepKind, today: ISODate): { pill: string; tone: ReminderTone } {
  if (step === "lands") return { pill: item.landsLabel ?? REMINDER_KINDS[item.kind].lands ?? "New", tone: "new" };
  if (step === "wait") {
    const n = item.lands ? daysBetween(dayOfStamp(item.lands), today) : 0;
    return { pill: `Waiting ${days(n)}`, tone: n > 0 ? "due" : "new" };
  }
  const n = item.date ? daysBetween(today, item.date) : 0;
  if (n < 0) return { pill: `${days(-n)} late`, tone: "late" };
  if (n === 0) return { pill: "Due today", tone: "due" };
  return { pill: n === 1 ? "Due tomorrow" : `Due in ${days(n)}`, tone: "due" };
}

export function reminderContent(item: ReminderItem, step: StepKind, today: ISODate): ReminderContent {
  const { pill, tone } = pillFor(item, step, today);
  const what = item.ref ? `${item.ref} ${short(item.title, 50)}` : short(item.title, 60);
  return { subject: `${what}: ${pill.toLowerCase()}`, pill, tone, title: item.title, facts: item.facts, open: item.open, href: item.href };
}

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

export interface ReminderLogEntry {
  id: string;
  at: string;
  sentAt: string | null;
  kind: ReminderKind;
  to: string;
  subject: string;
  href: string;
  status: "sending" | "sent" | "failed";
  attempts: number;
  error: string | null;
}

export interface ReminderState {
  mail: { mode: "live" | "demo" | "off"; ready: boolean; from: string | null };
  settings: ReminderSettings;
  log: ReminderLogEntry[];
  planned: (PlannedReminder & { toName: string; kindLabel: string })[];
}

/* ------------------------------------------------------------------ */
/* Coming up                                                           */
/* ------------------------------------------------------------------ */

export interface PlannedReminder {
  on: ISODate;
  kind: ReminderKind;
  itemId: string;
  to: string;
  ref: string | null;
  title: string;
  pill: string;
}

const isWorking = (d: ISODate, holidays: ReadonlySet<string>) => {
  const w = new Date(`${d}T00:00:00Z`).getUTCDay();
  return w !== 0 && w !== 6 && !holidays.has(d);
};
export const nextWorkingDay = (d: ISODate, holidays: ReadonlySet<string>) => {
  let x = d;
  while (!isWorking(x, holidays)) x = addDays(x, 1);
  return x;
};
export const isWorkingDay = isWorking;

/**
 * What the dated steps will send from `from` to `to`, as things stand: each
 * step on its working day, one a day per item and person. Steps that wait
 * on something happening (an item landing) can't be foreseen.
 */
export function plannedReminders(items: ReminderItem[], s: ReminderSettings, from: ISODate, to: ISODate, holidays: ReadonlySet<string>): PlannedReminder[] {
  if (!s.since) return [];
  const rules = rulesOf(s);
  const since = dayOfStamp(s.since);
  const out = new Map<string, PlannedReminder>();
  for (const item of items) {
    const r = rules.find((x) => x.kind === item.kind)!;
    for (const st of stepsFor(item, r, to)) {
      if (st.step === "lands") continue;
      const on = nextWorkingDay(st.on < since ? since : st.on, holidays);
      if (st.on < since || on < from || on > to) continue;
      for (const who of item.to) out.set(`${item.kind}:${item.id}:${who}:${on}`, { on, kind: item.kind, itemId: item.id, to: who, ref: item.ref, title: item.title, pill: pillFor(item, st.step, on).pill });
    }
  }
  return [...out.values()].sort((a, b) => a.on.localeCompare(b.on) || (a.ref ?? a.title).localeCompare(b.ref ?? b.title));
}
